import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { draftClaimLineItemContext } from '~/app/features/crew/claim-line-items/claimLineItemMiddleware';
import { crewClaimContext } from '~/app/features/crew/crewClaimMiddleware';
import { crewOrderContext } from '~/app/features/order-middleware/orderMiddleware';
import { settingsContext } from '~/app/settingsMiddleware';
import api from '~/utils/api';
import { getClaimReasons } from '~/utils/claimLineItem';
import { ClaimReasonCategoryCode } from '~/utils/constants';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { AsyncReturnType } from '~/utils/types';

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const { claimType } = context.get(crewClaimContext);
  const draftClaimLineItem = context.get(draftClaimLineItemContext);

  return {
    draftClaimLineItem,
    reasons: getClaimReasons(claimType, draftClaimLineItem).map(
      ({ id, name }) => ({
        name,
        id,
      }),
    ),
  };
});

export const INPUT_NAME = 'reasonId';

const formSchema = zfd.formData({
  [INPUT_NAME]: zfd.numeric(
    z
      .number({ message: 'Please choose a reason.' })
      .int({ message: 'Invalid reason ID.' })
      .positive({ message: 'Invalid reason ID.' }),
  ),
});

export const { action, useActionResult } = makeAction(
  async ({ formData, context }, appStore) => {
    const { claimType } = context.get(crewClaimContext);
    const draftClaimLineItem = context.get(draftClaimLineItemContext);
    const { storefrontId } = context.get(settingsContext);
    const order = context.get(crewOrderContext);
    const result = formSchema.safeParse(formData);

    if (!result.success) {
      return {
        ok: false,
        message: result.error.message,
      };
    }

    const { reasonId } = result.data;

    const reason = getClaimReasons(claimType, draftClaimLineItem).find(
      ({ id }) => id === reasonId,
    );

    if (!reason) {
      return {
        ok: false,
        message: 'Invalid claim reason.',
      };
    }

    appStore.dispatch(
      appStore.actions.draftClaimLineItem.setReason({
        reasonId,
        reasonCategoryCode: reason.category.code,
      }),
    );

    let calculatedReasonResp: AsyncReturnType<
      typeof api.calculateAfterReasonSelection
    > = {
      eventPolicyEnforcement: null,
      eventCustomFields: null,
    };

    // ? maybe should check if the reason has changed?
    if (reason.claimReasonDetails.length === 0) {
      // calculate the after reason selection to determine if we need to show the policy or custom fields step to add to state
      calculatedReasonResp = await api.calculateAfterReasonSelection({
        params: { storefrontId },
        body: {
          id: draftClaimLineItem.lineItem.id,
          claimType,
          calculatedLineItems: [
            {
              originalStoreOrderLineItemId: draftClaimLineItem.lineItem.id,
              quantity: draftClaimLineItem.quantity,
              grams: draftClaimLineItem.grams,
              unitPrice: draftClaimLineItem.unitPrice,
              unitTax: draftClaimLineItem.unitTax,
              comments: draftClaimLineItem.comments,
              reasonId: reason.id,
              reasonCategoryCode: reason.category.code,
              advancedExchangeMethod: draftClaimLineItem.advancedExchangeMethod,
            },
          ],
        },
      });

      appStore.dispatch(
        appStore.actions.draftClaimLineItem.setReasonPolicy(
          calculatedReasonResp.eventPolicyEnforcement,
        ),
      );

      appStore.dispatch(
        appStore.actions.draftClaimLineItem.setReasonCustomFields(
          calculatedReasonResp.eventCustomFields,
        ),
      );

      appStore.dispatch(
        appStore.actions.draftClaimLineItem.setOmittedResolutionMethods(
          calculatedReasonResp.omittedResolutionMethods,
        ),
      );
    }

    return (
      match({
        reason,
        order,
        policy: calculatedReasonResp.eventPolicyEnforcement,
      })
        // order has shipping protection and reason is a shipping issue
        .with(
          {
            reason: {
              category: { code: ClaimReasonCategoryCode.shippingIssue },
            },
            order: { wasShippingProtected: true },
          },
          () => redirect('../shipping-protection'),
        )
        // has at least one reason detail, go to reason detail selection step
        .with({ reason: { claimReasonDetails: [P.any, ...P.array()] } }, () =>
          redirect(`${reasonId}`),
        )
        //check if we need to show the policy if exist, go to reason follow up step
        .with({ policy: P.not(P.nullish) }, () => redirect('../follow-up'))
        // other wis
        .otherwise(() => redirect('../meta'))
    );
  },
);
